import { v4 as uuid } from 'uuid';
import { ApiStoryResponse, ApiTransitionElementResponse } from '../../../../../api/core/models';
import { DEFAULT_TARGET_PRECISION } from '../../../../../app.constants';
import { getElementIconPath } from '../../../../../functions/transition-targets/get-element-details';
import { conditionallyPrefixImagesWithFilesUrl } from '../../../../../functions/utilities/img-url-utility';
import { ClientNode } from '../../../../../model/client-model/client-node';
import { ModelType } from '../../../../../model/client-model/properties/model-type';
import { NodeType } from '../../../../../model/client-model/properties/node-type';
import { TransitionElement } from '../../../../../model/enums/transition-element';
import { IndicatorImageSource } from '../../../../../model/indicator-image-source';
import { IndicatorListItem } from '../../../../../model/indicator-list-item';
import { curveTypeToTrajectoryFunctionType } from '../../../../../store/transition-elements/utils';
import { storyHistoricalToHistoricalEmissionsData } from './story-historical-to-historical-emissions-data';

export const transitionElementToClientNode = (
  group: ApiTransitionElementResponse,
  elementFromStore: ApiTransitionElementResponse,
  boardId: string,
  isSupply: boolean = false,
  baseUrlFiles?: string
): { node: ClientNode; indicator: IndicatorListItem } => {
  const nodeStory: ApiStoryResponse = group?.story ?? { id: group.id, title: '' };
  const historicalEmissionData = storyHistoricalToHistoricalEmissionsData(nodeStory?.historicalEmissions ?? {});
  const prefixIfSet = (url: string | undefined): string => {
    if (!url) {
      return '';
    }
    if (!baseUrlFiles) {
      return url;
    }
    return conditionallyPrefixImagesWithFilesUrl(url, baseUrlFiles);
  };
  const indicatorId = uuid();
  const indicator: IndicatorListItem = {
    id: indicatorId,
    _etag: '',
    _ts: 0,
    title: '',
    indicatorProperties: {
      diff: elementFromStore.assessment?.data ?? 0,
      unknownDiff: elementFromStore.assessment?.isUnknown ?? false,
      assessmentDescription: elementFromStore.assessment?.about ?? '',
      potentialDescription: elementFromStore.diagram?.about ?? '',
      description: elementFromStore.diagram?.moreInfo ?? '',
      outcomeDescription: elementFromStore.diagram?.reference ?? '',
      goalDescription: elementFromStore.target?.goalDescription ?? '',
      imgSource: IndicatorImageSource.Data,
    },
  };
  const node: ClientNode = {
    id: group.id,
    indicators: [indicatorId],
    actions: group.actionIds ?? [],
    boardId,
    title: nodeStory.title,
    childNodes: [],
    _etag: '',
    _ts: 0,
    nodeProperties: {
      co2e: 0,
      data: [],
      description: '',
      diagramHeader: nodeStory?.diagramHeader ?? '',
      extendedDescription: {
        data: {
          texts: [{ body: nodeStory?.description ?? '', title: nodeStory?.title ?? '' }],
          images: [{ imgURL: prefixIfSet(nodeStory?.imgUrl), imgCredits: nodeStory?.imageCredits ?? '' }],
        },
        type: 1,
      },
      diff: 0,
      icon: getElementIconPath(group),
      imgURL: '',
      modelType: ModelType.TRANSITION_ELEMENT,
      thumbnailURL: '',
      transitionElement: group.transitionElement as unknown as TransitionElement,
      transitionTargetDescription: nodeStory?.description ?? '',
      transitionTargetData: {
        transitionOutcome: {
          outcome: elementFromStore.progress?.find((p) => p.targetType === elementFromStore.targetType)?.data ?? {},
        },
        transitionProperties: {
          targetLevel: elementFromStore.target.targetLevel,
          transitionPath: {
            type: curveTypeToTrajectoryFunctionType(elementFromStore.target.curveType),
            ...(Object.keys(elementFromStore.target?.targets ?? {}).length > 0
              ? { manualPath: Object.values(elementFromStore.target.targets ?? []) }
              : {}),
          },
          weightType: elementFromStore.targetType ?? (<any>elementFromStore.target).targetType, // taking regard to old interfaces
          displayTargetLevel: elementFromStore?.target.displayTargetLevel ?? undefined,
        },
        transitionTargetProperties: {
          transitionElement: group.transitionElement as unknown as TransitionElement,
        },
        precision: DEFAULT_TARGET_PRECISION,
      },
      achievableLater: false,
      type: isSupply ? NodeType.SupplyTarget : NodeType.Target,
      historicalEmissionData,
    },
  };
  return { node, indicator };
};
